import { css } from "@emotion/css";
import { uniqueId } from "lodash";
import { useEffect, useRef, useState } from "react";

export function generateClassName() {
  return uniqueId("generated-");
}

interface MultiStageTransitionOptions {
  stages: {
    className?: string;
    startingDelay?: number;
  }[];
}

interface ConsumableStage {
  className: string;
  startingDelay?: number;
}

function mapWithPrevResult<T, U>(array: T[], fn: (t: T, prev: U | null) => U): U[] {
  let prev: U | null = null;
  return array.map((val) => {
    prev = fn(val, prev);
    return prev;
  });
}

export function compileMultiStageTransition(
  options: MultiStageTransitionOptions,
): ConsumableStage[] {
  return mapWithPrevResult(
    options.stages,
    (stage, prev: ConsumableStage | null): ConsumableStage => {
      // Note: rolling each result forward like this guarantees that the
      // final transition: for the same property wins. (This is a feature of Emotion)
      return {
        startingDelay: stage.startingDelay,
        className: css`
          ${prev?.className};
          ${stage.className};
        `,
      };
    },
  );
}

export function useOnScreen<E extends HTMLElement = HTMLElement>(): [boolean, React.Ref<E>] {
  const onScreenRef = useRef<E>(null);
  const [onScreen, setOnScreen] = useState(false);
  useEffect(() => {
    // TODO (alex): swapping root refs woult be a problem for us. It's not clear to me how you're actually
    // supposed to consume refs in a side-effectful way

    const element = onScreenRef.current;
    if (element == null) {
      console.error("Error: no ref object for useOnScreen()");
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      setOnScreen(entry.isIntersecting);
    });

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  return [onScreen, onScreenRef] as const;
}

export function useStageAdvancing<T extends { startingDelay?: number }>({
  stages,
  numStages,
  paused,
  stageDurationMs,
  mode,
}: {
  stages: T[];
  numStages: number;
  stageDurationMs: number;
  paused: boolean;
  mode: "forwards" | "cycle";
}): [number, (stage: number) => void] {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (paused || stage === numStages - 1) return;

    const advance = () =>
      setStage(mode === "forwards" ? Math.min(numStages - 1, stage + 1) : (stage + 1) % numStages);

    const timeout = setTimeout(advance, stages[stage + 1]?.startingDelay ?? stageDurationMs);

    return () => clearTimeout(timeout);
  }, [paused, stage, setStage, numStages, stages, stageDurationMs, mode]);

  return [stage, setStage];
}
